import { ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { PhoneNumberUtil, PhoneNumberType } from 'google-libphonenumber';

export interface PhoneNumberTypeError {
  expected: PhoneNumberType;
  actual: PhoneNumberType;
}

export abstract class NomidioPhoneValidators {
  public static readonly PhoneNumberType = PhoneNumberType;
  protected static readonly phoneUtil = PhoneNumberUtil.getInstance();
  /**
   * Validator that requires the control's value is a valid E164182357 phone number, according to google-libphonenumber.
   *
   * @returns A validator that returns `{ phone: true }` if the phone number is invalid, or `null` if there's no issues.
   */
  public static get phone(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const valid = NomidioPhoneValidators.validatePhoneNumber(control.value);

      if (!valid) {
        return { phone: true };
      }

      return null;
    };
  }

  /**
   * Construct a validator that requires the control's value is a certain type of phone number.
   *
   * This uses google-libphonenumber to check.
   *
   * This does not guarantee the phone number is necessarily valid. To check validity, use the {@link NomidioPhoneValidators.phone} validator.
   *
   * @param phoneNumberType The phone number type to require
   * @returns A validator that returns `{ phonetype: PhoneNumberType }` if the number is valid but does not match the required type
   */
  public static phoneType(phoneNumberType: PhoneNumberType): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      try {
        const parsed = NomidioPhoneValidators.phoneUtil.parse(control.value);
        const type = NomidioPhoneValidators.phoneUtil.getNumberType(parsed);
        if (type !== PhoneNumberType.UNKNOWN && type !== phoneNumberType) {
          const errorFeedback: PhoneNumberTypeError = {
            expected: phoneNumberType,
            actual: type
          };
          return { phonetype: errorFeedback };
        }
        return null;
      } catch {
        return null;
      }
    };
  }

  /**
   * Check whether a given number is a valid phone number.
   *
   * @param value The phone number to check
   * @returns Whether the phone number is valid
   */
  public static validatePhoneNumber(value: string): boolean {
    try {
      const phoneNumber = NomidioPhoneValidators.phoneUtil.parse(value);
      return NomidioPhoneValidators.phoneUtil.isValidNumber(phoneNumber);
    } catch {
      return false;
    }
  }

}
